import { useEffect, useState, useCallback } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { SimpleButton, ControlledRadio, ErrorMessage } from '@/src/ui';
import {
    ControlledDynamicTextEditor,
    isEditorContentEmpty,
    MultipleChoiceRenderer,
    LETTERS,
    type QtiMultipleChoiceQuestionDTO,
    type QuestionMetaDTO,
} from '@/src/lib';
import { defaultMeta, type ChoiceDTO } from '../../models/questionDTOs';
import { getChoiceSchemaDTE, metaSchema, areItemsUnique } from '../../models/schema';
import {
    Debugger,
    QuestionMetaForm,
    TextEntryModal,
    QuestionTitleField,
    type TextEntryModalPartialProps,
} from '../_shared';
import { saveQuestion } from '../../services';
import { renderLoaders } from '../../utils';
import { Constants } from '@/src/utils';
const qtm = Constants.QUESTION_TYPE_MASTER_IDS;

const config = {
    minAnswers: 2,
    maxAnswers: 10,
};

const schema = yup.object().shape({
    id: yup.number().optional(),
    title: yup
        .string()
        .trim()
        .required('Title is required')
        .max(
            Constants.MAX_CHARS.TITLE,
            `Title cannot exceed ${Constants.MAX_CHARS.TITLE} characters`
        ),
    prompt: yup
        .string()
        .test(
            'not-empty',
            'Prompt cannot be empty.',
            (value) => !isEditorContentEmpty(value || '')
        ),
    prompt_es: yup.string().when('$isSpanishAllowed', {
        is: true,
        then: (schema) =>
            schema
                .test(
                    'not-empty',
                    'Prompt (Spanish) cannot be empty.',
                    (value) => !isEditorContentEmpty(value || '')
                )
                .required('Prompt (Spanish) is required'),
        otherwise: (schema) => schema.optional(),
    }),
    choices: yup
        .array()
        .of(getChoiceSchemaDTE('Choice text'))
        .required()
        .min(config.minAnswers, `At least ${config.minAnswers} choices are required`)
        .max(config.maxAnswers, `A maximum of ${config.maxAnswers} choices is allowed`)
        .test('unique', 'Choices must be unique.', (choiceList) =>
            areItemsUnique(choiceList, 'text')
        )
        .required('Choices are required')
        .when('$isSpanishAllowed', {
            is: true,
            then: (schema) =>
                schema.test('unique', 'Spanish choices must be unique.', (choiceList) =>
                    areItemsUnique(choiceList, 'text_es')
                ),
            otherwise: (schema) => schema,
        }),
    correctAnswers: yup
        .array()
        .of(yup.number().required())
        .min(1, 'At least one choice must be marked as correct')
        .required('At least one correct answer is required'),
    meta: metaSchema.required(),
});

interface MultipleChoiceEditorProps {
    initialModel?: QtiMultipleChoiceQuestionDTO;
}

export default function MultipleChoiceEditor({ initialModel }: MultipleChoiceEditorProps) {
    const isEdit = !!initialModel;
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isTitleError, setIsTitleError] = useState(false);
    const [modalProps, setModalProps] = useState<TextEntryModalPartialProps | null>(null);

    const [nextChoiceId, setNextChoiceId] = useState<number>(5);

    // Default choices for new question
    const defaultChoices: ChoiceDTO[] = Array.from({ length: 4 }, (_, index) => ({
        id: index + 1,
        text: '',
        text_es: '',
    }));
    const defaultValues: QtiMultipleChoiceQuestionDTO = initialModel
        ? {
              ...initialModel,
              selectedCorrectAnswer: initialModel.correctAnswers?.[0],
          }
        : {
              title: '',
              prompt: '',
              prompt_es: '',
              choices: defaultChoices,
              correctAnswers: [],
              meta: { ...defaultMeta, questionTypeMasterId: qtm.MULTIPLE_CHOICE },
          };

    const [formContext, setFormContext] = useState({
        isSpanishAllowed: defaultValues.meta.isSpanishAllowed,
    });
    const {
        control,
        handleSubmit,
        setValue,
        getValues,
        watch,
        formState: { errors, isSubmitting },
        setError: setFormError,
        clearErrors,
    } = useForm<QtiMultipleChoiceQuestionDTO>({
        defaultValues,
        resolver: yupResolver(schema as any),
        mode: 'onBlur',
        context: formContext,
    });

    const question = watch();
    useEffect(() => {
        setFormContext({ isSpanishAllowed: question.meta.isSpanishAllowed });
    }, [question.meta.isSpanishAllowed]);

    const { append, remove } = useFieldArray({
        control,
        name: 'choices',
    });
    const choices = watch('choices');

    const handleAddChoice = () => {
        if (choices.length >= config.maxAnswers) return;
        append({ id: nextChoiceId, text: '', text_es: '' });
        setNextChoiceId((prev) => prev + 1);
    };

    const handleRemoveChoice = (index: number) => {
        if (choices.length === config.minAnswers) return;
        remove(index);
        // After removal, update correctAnswers
        const newChoices = getValues('choices');
        const correctAnswers = getValues('correctAnswers');
        const newCorrect = newChoices.filter((c) => correctAnswers.includes(c.id)).map((c) => c.id);
        setValue('correctAnswers', newCorrect);
    };

    const handleLanguageSet = useCallback(
        (language: 'en' | 'es') => {
            if (language === 'en') return;

            if (!question.prompt_es) {
                setValue('prompt_es', question.prompt);
            }
            question.choices.forEach((choice, index) => {
                if (!choice.text_es) {
                    setValue(`choices.${index}.text_es`, choice.text);
                }
            });
        },
        [question, setValue]
    );

    const onSubmit = async () => {
        if (success) return;
        if (isTitleError) {
            setError('Please choose a unique title.');
            return;
        }

        clearErrors();
        setLoading(true);
        setError(null);
        try {
            const viewMode = isEdit ? 'edit' : 'create';
            await saveQuestion(question, viewMode);
            setSuccess(true);
        } catch (e: any) {
            setError(e.message || 'Unknown error');
            setFormError('root', { type: 'manual', message: e.message || 'Unknown error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <form
            key={`MultipleChoiceEditor-${question.meta.isSpanishAllowed}`}
            className="p-4 space-y-4"
            onSubmit={handleSubmit(onSubmit)}
        >
            {/* Handles all Spanish entry */}
            {modalProps && (
                <TextEntryModal
                    initialData={modalProps.initialData}
                    fieldName={modalProps.fieldName}
                    friendlyName={modalProps.friendlyName}
                    textMode={modalProps.textMode}
                    error={modalProps.error}
                    control={control}
                    handleClose={() => setModalProps(null)}
                />
            )}
            {renderLoaders(loading, success, error, isEdit)}

            <h2>Multiple Choice Question Title:</h2>
            <QuestionTitleField
                id={question.id}
                title={question.title}
                control={control}
                setIsTitleError={setIsTitleError}
                error={errors.title}
            />

            <div className="my-4" />

            <div className="flex flex-row gap-4">
                <h2>Prompt:</h2>
                {question.meta.isSpanishAllowed && (
                    <div
                        onClick={() =>
                            setModalProps({
                                initialData: question.prompt_es,
                                fieldName: 'prompt_es',
                                friendlyName: 'Prompt (Spanish)',
                                textMode: 'rich',
                                error: errors.prompt_es?.message,
                            })
                        }
                        style={{ cursor: 'pointer' }}
                    >
                        Edit Spanish
                    </div>
                )}
                <ErrorMessage error={errors.prompt_es} />
            </div>

            <ControlledDynamicTextEditor
                name="prompt"
                control={control}
                initialValue={question.prompt}
                style={{ minHeight: '150px' }}
            />
            <ErrorMessage error={errors.prompt} />

            <div className="my-4" />

            <h2>Choices:</h2>
            {choices.map((choice, index) => {
                const choiceId = choice.id;
                return (
                    <div key={`mc-choice-${choiceId}`}>
                        <div className="flex items-center gap-6">
                            <span className="font-bold w-6 text-center">
                                {LETTERS[index] || '?'}{' '}
                            </span>
                            <div>
                                {question.meta.isSpanishAllowed && (
                                    <div
                                        onClick={() =>
                                            setModalProps({
                                                initialData: question.choices[index]?.text_es,
                                                fieldName: `choices.${index}.text_es`,
                                                friendlyName: `Choice ${LETTERS[index]} (Spanish)`,
                                                textMode: 'rich',
                                                error: errors.choices?.[index]?.text_es?.message,
                                            })
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Edit Spanish
                                    </div>
                                )}
                                <ErrorMessage error={errors.choices?.[index]?.text_es} />
                                <ControlledDynamicTextEditor
                                    name={`choices.${index}.text`}
                                    control={control}
                                    initialValue={question.choices[index]?.text}
                                    style={{ minHeight: '50px', minWidth: '400px' }}
                                />
                            </div>
                            <ControlledRadio
                                name="selectedCorrectAnswer"
                                label="Correct"
                                control={control}
                                onChange={(_e, field) => {
                                    setValue('correctAnswers', [choiceId]);
                                    field.onChange(choiceId);
                                }}
                                checkedValue={choiceId}
                            />
                            <SimpleButton
                                type="button"
                                color="error"
                                variant="outlined"
                                className="text-red-500"
                                disabled={choices.length <= config.minAnswers}
                                onClick={() => handleRemoveChoice(index)}
                            >
                                Remove
                            </SimpleButton>
                        </div>
                        <ErrorMessage error={errors.choices?.[index]?.text} />
                    </div>
                );
            })}
            <ErrorMessage error={errors.choices?.root} />
            <ErrorMessage error={errors.choices} />
            <ErrorMessage error={errors.correctAnswers} />

            <SimpleButton
                type="button"
                color="primary"
                onClick={handleAddChoice}
                className="bg-blue-500 text-white px-4 py-2 rounded"
                disabled={choices.length >= config.maxAnswers}
            >
                Add Choice
            </SimpleButton>

            <div className="my-4" />

            <QuestionMetaForm
                rhfProps={{
                    control,
                    errors: errors.meta,
                    watch,
                }}
                handleLanguageSet={handleLanguageSet}
                metaValues={watch('meta')}
                disableList={['isHandScored', 'shouldShowKeypad']}
                setMetaValues={(meta: QuestionMetaDTO) => setValue('meta', meta)}
            />

            <div className="border rounded p-4 bg-white max-w-4xl space-y-4 mt-4">
                <h4 className="font-semibold mb-1">Interactive Preview</h4>
                <MultipleChoiceRenderer
                    question={question}
                    options={{
                        showLanguageToggle: question.meta.isSpanishAllowed,
                        showScoring: true,
                    }}
                />
            </div>

            <div className="flex items-center space-x-2">
                <div>
                    <SimpleButton
                        type="submit"
                        color="primary"
                        className="bg-green-600 text-white px-3 py-1 rounded text-xs"
                        disabled={isSubmitting || loading}
                    >
                        {isEdit ? 'Update Question' : 'Create Question'}
                    </SimpleButton>
                </div>
            </div>
            <ErrorMessage
                error={Object.keys(errors).length ? 'Please correct the errors to continue' : ''}
            />
            {renderLoaders(loading, success, error, isEdit)}

            <Debugger question={question} errors={errors} />
        </form>
    );
}
